import { Injectable } from '@angular/core';
import { Observable, map, of, tap, catchError, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	AudioChannelFeatureUpdateInput,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import {
	ChannelNonLinearProcessingLevel,
	Response,
	ResponseState,
	SpatialSeparation
} from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { AcousticEchoCancellationChannel } from '../../api/models';
import { AcousticEchoCancellationApiService } from '../../api/services';
import {
	AecChannelMutationGQL,
	AecChannelQueryGQL,
	AecChannelSubscriptionGQL,
	AecMonoReferenceMutationGQL,
	AecStereoReferenceMutationGQL
} from '../graphql/generated/aec.generated';
import { mapAecChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspAecService implements AcousticEchoCancellationApiService {
	private readonly logger: ILogger;

	private readonly aecWithReferenceChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly aecWithoutReferenceChannelSubscriptionManager: CombiningSubscriptionManager;
	private readonly meterSourceChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly aecChannelQueryGQL: AecChannelQueryGQL,
		private readonly aecChannelMutationGQL: AecChannelMutationGQL,
		private readonly aecMonoReferenceMutationGQL: AecMonoReferenceMutationGQL,
		private readonly aecStereoReferenceMutationGQL: AecStereoReferenceMutationGQL,
		aecChannelSubscriptionGQL: AecChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspAecService');

		this.aecWithReferenceChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspAecService:WithAec',
				changeTypes: [
					NodeChangeType.AudioChannelAcousticEchoCancellation,
					NodeChangeType.AudioChannelAcousticEchoCancellationMonoReference,
					NodeChangeType.AudioChannelAcousticEchoCancellationStereoReference,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: aecChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.aecWithoutReferenceChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspAecService:WithoutAec',
				changeTypes: [
					NodeChangeType.AudioChannelAcousticEchoCancellation,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: aecChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);

		this.meterSourceChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspAecService:MeterSource',
				changeTypes: [
					NodeChangeType.AudioChannelAutomixerInputSendToMonoMix,
					NodeChangeType.AudioChannelLobeConfiguration
				],
				subscriptionGql: aecChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<AcousticEchoCancellationChannel> {
		const queryId = uuidv4();
		return this.aecChannelQueryGQL
			.watch({ channelId }, { fetchPolicy: 'network-only', errorPolicy: 'all' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$', 'Query updated', { channelId });
						return mapAecChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((channel) => {
					if (
						channel.constraints.acousticEchoCancellationMonoReference.referenceSourceOptions.supported ||
						channel.constraints.acousticEchoCancellationStereoReference.referenceSourceOptions.supported
					) {
						this.aecWithReferenceChannelSubscriptionManager.subscribe(queryId, channel.id);
					} else {
						this.aecWithoutReferenceChannelSubscriptionManager.subscribe(queryId, channel.id);
					}

					if (channel.features.acousticEchoCancellationErleMeterSource) {
						channel.features.acousticEchoCancellationErleMeterSource.channels.forEach((channel) => {
							this.meterSourceChannelSubscriptionManager.subscribe(queryId, channel.id);
						});
					}
				}),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.aecWithReferenceChannelSubscriptionManager.unsubscribeQuery(queryId);
					this.aecWithoutReferenceChannelSubscriptionManager.unsubscribeQuery(queryId);
					this.meterSourceChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public toggleAec(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleAec()', 'Toggling aec', { channelId, enabled });
		return this.updateChannel(channelId, { acousticEchoCancellation: { enabled } });
	}

	public updateAecLevel(
		channelId: string,
		level: ChannelNonLinearProcessingLevel
	): Observable<Response<void, string>> {
		this.logger.trace('updateAecLevel()', 'Updating aec level', { channelId, level });
		return this.updateChannel(channelId, { acousticEchoCancellation: { nonLinearProcessingLevel: level } });
	}

	public updateMonoReferenceChannel(
		channelId: string,
		referenceChannelId: string
	): Observable<Response<void, string>> {
		this.logger.trace('updateReferenceChannel()', 'Updating aec reference channel', {
			channelId,
			referenceChannelId
		});

		return this.aecMonoReferenceMutationGQL
			.mutate(
				{
					input: {
						id: channelId,
						source: referenceChannelId
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateAcousticEchoCancellationMonoReferenceSource &&
						'error' in mutation.data.updateAcousticEchoCancellationMonoReferenceSource &&
						mutation.data.updateAcousticEchoCancellationMonoReferenceSource.error !== null
					) {
						this.logger.error('updateReferenceChannel()', 'Failed to update aec reference channel', {
							channelId,
							mutation
						});
						return {
							state: ResponseState.Error,
							error: mutation.data.updateAcousticEchoCancellationMonoReferenceSource.error.message
						};
					}

					this.logger.trace('updateReferenceChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateReferenceChannel()', 'Failed to update reference channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}

	public updateStereoReferenceChannel(
		channelId: string,
		referenceChannelId: string,
		spatialSeparation: SpatialSeparation
	): Observable<Response<void, string>> {
		this.logger.trace('updateStereoReferenceChannel()', 'Updating aec reference channel', {
			channelId,
			referenceChannelId,
			spatialSeparation
		});

		return this.aecStereoReferenceMutationGQL
			.mutate(
				{
					input: {
						id: channelId,
						source: referenceChannelId,
						stereoAudioChannelSpatialSeparation: spatialSeparation
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateAcousticEchoCancellationStereoReferenceSource &&
						'error' in mutation.data.updateAcousticEchoCancellationStereoReferenceSource &&
						mutation.data.updateAcousticEchoCancellationStereoReferenceSource.error !== null
					) {
						this.logger.error('updateStereoReferenceChannel()', 'Failed to update aec reference channel', {
							channelId,
							mutation
						});
						return {
							state: ResponseState.Error,
							error: mutation.data.updateAcousticEchoCancellationStereoReferenceSource.error.message
						};
					}

					this.logger.trace('updateStereoReferenceChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateStereoReferenceChannel()', 'Failed to update reference channel', {
						error
					});
					return of({ state: ResponseState.Error });
				})
			);
	}

	private updateChannel(
		channelId: string,
		update: AudioChannelFeatureUpdateInput
	): Observable<Response<void, string>> {
		return this.aecChannelMutationGQL
			.mutate(
				{
					updates: { audioChannel: { id: channelId, features: { ...update } } },
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('toggleAec()', 'Failed to toggle aec', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('toggleAec()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error });
				})
			);
	}
}
